<template>
  <!-- Container -->
  <div class="paginated-table">
    <!-- Header of the component. -->
    <div>
      <b-row class="align-items-center" :align-h="label ? 'start' : 'end'">
        <b-col v-if="label || $slots.head" cols="12" lg
          ><slot name="head">
            <h2>{{ label | capitalize }}</h2>
          </slot>
        </b-col>
        <b-col cols="auto" class="button-list">
          <slot name="head-buttons"> </slot>

          <b-button-group>
            <icon-button
              v-if="showColumnSelection"
              v-b-modal="columnModalId"
              v-b-tooltip:hover="$t('components.shared.paginated_table.columns.toggle')"
              variant="ghost-secondary"
              icon="layout-three-columns"
            />

            <icon-button
              v-if="hasFilters"
              class="toggle-filters-button"
              variant="ghost-secondary"
              :pressed="showFilters"
              icon="filter"
              @click="toggleFilters"
            >
              {{ $t("components.shared.paginated_table.filters.label") }}
              <b-badge v-if="activeFilterCount > 0" class="ml-2" variant="danger">{{
                activeFilterCount
              }}</b-badge>
            </icon-button>

            <icon-button
              v-if="differsFromDefault"
              v-b-tooltip:hover="$t('components.shared.paginated_table.filters.reset')"
              variant="ghost-secondary"
              icon="skip-start-circle"
              @click="resetToDefaults"
            >
            </icon-button>
          </b-button-group>

          <div
            v-b-tooltip:hover="
              $t('components.shared.paginated_table.filters.last_update', {
                minutes: minutesSinceLastFetch,
              })
            "
            class="d-inline-block"
            @mouseenter="calculateTimeSinceLastFetch"
          >
            <icon-button
              class="refresh-button"
              variant="ghost-secondary"
              :loading="isBusy"
              icon="arrow-clockwise"
              spin-icon
              @click="() => refresh()"
            />
          </div>
        </b-col>
      </b-row>

      <b-card v-if="showFilters && detachedFilters.length > 0" class="filter-list">
        <b-row>
          <b-col v-for="filter of detachedFilters" :key="filter.field" class="filter" lg="3" md="6">
            <b-form-group :key="filter.field" label-size="sm" :label="filter.label">
              <filter-input
                v-model="filters[filter.field]"
                :type="filter.type"
                :name="filter.field"
                :options="filter.options"
                @input="() => $emit('input:filters', filters)"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <!-- Column visibility modal -->
    <b-modal
      :id="columnModalId"
      :title="$t('components.shared.paginated_table.columns.title')"
      ok-only
    >
      <b-checkbox-group
        v-model="columnsToShow"
        :options="columnsOptions"
        switches
        stacked
      ></b-checkbox-group>
    </b-modal>

    <!-- Lazy-load component only loads data when component is in view. -->
    <lazy-load @load="fetchItems">
      <b-table
        ref="table"
        :busy="isBusy"
        class="paginated-table"
        :class="{ 'with-filters': showFilters && detachedFilters.length > 0 }"
        :primary-key="primaryKey"
        responsive
        :hover="hasRowDetails || rowLink || hasRowClickedListener"
        no-local-sorting
        :api-url="endpoint"
        :fields="shownColumns"
        :items="items"
        :show-empty="true"
        :empty-filtered-text="$t('components.shared.paginated_table.empty_text')"
        :empty-text="$t('components.shared.paginated_table.empty_text')"
        :current-page="currentPage"
        :per-page="0"
        :filter="filters"
        v-bind="$attrs"
        :selectable="selectable"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        sort-icon-left
        label-sort-asc=""
        label-sort-desc=""
        label-sort-clear=""
        borderless
        no-select-on-click
        :table-class="{
          'row-clickable': hasRowDetails || rowLink || hasRowClickedListener,
          'has-row-details': hasRowDetails,
        }"
        :tbody-tr-class="computeRowClasses"
        @row-selected="rowSelected"
        @sort-changed="currentPage = 1"
        @row-clicked="rowClicked"
      >
        <!-- Cells of thead represented as <th> -->
        <template v-for="column in shownColumns" #[column.head]="data">
          <div :key="column.key" class="header-wrapper">
            <!-- Column title, with a checkbox if column is selectable. -->
            <div class="header-label">
              <b-form-checkbox
                v-if="selectable && column.key === keyColumn"
                size="sm"
                class="mr-0 d-inline-block"
                :indeterminate="selectedCount > 0 && itemCount !== selectedCount"
                :checked="itemCount > 0 && itemCount === selectedCount"
                @change="toggleSelection"
              />
              {{ column.key !== "actions" ? data.label : "" }}
            </div>

            <!-- Filter input in table cell if filters are shown -->
            <div v-if="showFilters && hasFilters && column.filter" class="mt-2">
              <slot :name="column.filtertemplate" :filters="filters">
                <filter-input
                  v-model="filters[column.filter.field]"
                  :class="column.class"
                  :type="column.filter.type"
                  :name="column.filter.field"
                  :options="column.filter.options"
                />
              </slot>
            </div>
          </div>
        </template>

        <!-- Cells of tbody -->
        <template v-for="column in shownColumns" #[column.cell]="rowData">
          <slot :name="column.cell" v-bind="rowData">
            <!-- Action column -->
            <template v-if="column.key === 'actions'">
              <div :key="column.key" class="text-right">
                <b-icon
                  v-if="hasRowDetails && !rowDisabled(rowData.item)"
                  class="details-chevron"
                  icon="chevron-right"
                  :class="{
                    'details-showing': rowData.detailsShowing,
                  }"
                />
                <span v-if="hasRowDetails"></span>
                <admin-list-actions
                  v-else
                  :slug="endpoint"
                  :item-name="`ce ${label}`"
                  :item="rowData.item"
                  :show-destroy="false"
                  :show-restore="false"
                />
              </div>
            </template>
            <!-- Key column -->
            <template v-else-if="column.key === keyColumn">
              <template v-if="selectable">
                <div
                  :key="column.key"
                  class="select-wrapper"
                  @click.prevent.stop="
                    () => (rowData.rowSelected ? rowData.unselectRow() : rowData.selectRow())
                  "
                >
                  <b-form-checkbox size="sm" class="d-inline-block" :checked="rowData.rowSelected">
                    <b-link v-if="rowLink" :to="`/admin/${endpoint}/${rowData.value}`">{{
                      rowData.value
                    }}</b-link>
                    <span v-else>{{ rowData.value }}</span>
                  </b-form-checkbox>
                </div>
              </template>
              <template v-else>
                <b-link
                  v-if="rowLink"
                  :key="column.key"
                  :to="`/admin/${endpoint}/${rowData.value}`"
                  >{{ rowData.value }}</b-link
                >
                <span v-else :key="column.key">{{ rowData.value }}</span>
              </template>
            </template>
            <!-- Regular column -->
            <template v-else>
              <template v-if="isUrlValid(column, rowData.item)">
                <a
                  v-if="isExternalUrl(column, rowData.item)"
                  :key="column.key"
                  :href="column.urlFct(rowData.item)"
                >
                  {{ rowData.value }}
                </a>
                <router-link v-else :key="column.key" :to="column.urlFct(rowData.item)">
                  {{ rowData.value }}
                </router-link>
              </template>
              <template v-else-if="rowData.value">
                <b-badge v-if="rowData.value.variant" :variant="rowData.value.variant">{{
                  rowData.value.text
                }}</b-badge>
                <template v-else>
                  {{ rowData.value }}
                </template>
              </template>
            </template>
          </slot>
        </template>

        <template #row-details="row">
          <div class="p-3">
            <slot
              name="row-details"
              v-bind="
                editableItems
                  ? { ...row, initialItem: getFetchedItemCopy(row.item), updateItem: updateItem }
                  : row
              "
            />
          </div>
        </template>
      </b-table>
    </lazy-load>

    <!-- Footer of the component -->
    <b-row>
      <b-col md="6">
        <slot name="moreExports" />
      </b-col>
      <b-col v-if="total > 10" md="6">
        <admin-pagination
          class="justify-content-end"
          :page="currentPage"
          :per-page="itemsPerPage"
          :total="total"
          @update:perPage="itemsPerPage = $event"
          @update:page="currentPage = $event"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import AdminListActions from "@/components/Admin/ListActions.vue";
import AdminPagination from "@/components/Admin/Pagination.vue";
import filterInput from "@/components/shared/FilterInput.vue";
import IconButton from "@/components/shared/IconButton.vue";
import LazyLoad from "@/components/shared/Lazy.vue";
import { Column, Filter } from "@/components/shared/PaginatedTableColumns";
import { debounce } from "@/helpers/debounce";
import { get } from "@/requests/server";
import dayjs from "dayjs";

let uniqueId = 0;

export default {
  name: "PaginatedTable",
  components: {
    IconButton,
    LazyLoad,
    AdminPagination,
    AdminListActions,
    filterInput,
  },
  props: {
    id: {
      type: String,
      default: undefined,
    },
    columns: {
      type: Array[Column],
      required: true,
    },
    extraFilters: {
      type: Array[Filter],
      required: false,
      default: () => [],
    },
    loadRelations: {
      type: Array[String],
      required: false,
      default: () => [],
    },
    countRelations: {
      type: Array[String],
      required: false,
      default: () => [],
    },
    selectable: { type: Boolean, default: false },
    label: {
      type: String,
      default: undefined,
    },
    endpoint: {
      type: String,
      required: true,
    },
    /**
     * Params to propagate directly to the request. Can set fixed filters which cannot be altered
     * with the UI.
     */
    fetchParams: {
      type: Object,
      default: () => ({}),
    },
    initialFilters: {
      type: Object,
      default: () => ({}),
    },
    syncFiltersWithUrl: {
      type: Boolean,
      default: false,
    },
    // Prefix for every filter key in the URL query
    urlPrefix: {
      type: String,
      default: null,
    },
    initialSortBy: {
      type: String,
      default: "id",
    },
    initialSortDesc: {
      type: Boolean,
      default: true,
    },
    showActionColumn: {
      type: Boolean,
      default: true,
    },
    showColumnSelection: {
      type: Boolean,
      default: true,
    },
    hasRowDetails: {
      type: Boolean,
      default: false,
    },
    rowLink: {
      type: Boolean,
      default: false,
    },
    keyColumn: {
      type: String,
      default: "id",
    },
    newItem: {
      type: Object,
      default: () => null,
    },
    rowDisabled: {
      type: Function,
      default: () => false,
    },
    editableItems: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let filters = null;
    let columnsToShow = null;

    const listState = this.id ? this.$store.state.paginatedTables[this.id] : {};
    filters = this.getSavedFiltersOrDefault(listState);
    if (this.syncFiltersWithUrl) {
      const urlFilters = this.getFiltersFromUrl();
      if (Object.keys(urlFilters).length > 0) {
        filters = urlFilters;
      }
    }
    columnsToShow = this.getSavedColumnsToShowOrDefault(listState);

    this.$emit("filters-changed", filters);

    return {
      columnsToShow,
      currentPage: listState?.currentPage ?? 1,
      fetchedItems: [],
      filters,
      isBusy: false,
      itemCount: 0,
      items: [],
      itemsCopy: [],
      itemsPerPage: listState?.perPage ?? 10,
      lastFetch: null,
      minutesSinceLastFetch: null,
      selectedCount: 0,
      showDetails: {},
      showFilters: false,
      sortBy: listState?.sortBy ?? this.initialSortBy,
      sortDesc: listState?.sortDesc ?? this.initialSortDesc,
      total: 0,
    };
  },
  computed: {
    columnModalId() {
      return `column-modal-${uniqueId}`;
    },
    hasRowClickedListener() {
      return this.$listeners && this.$listeners["row-clicked"];
    },
    columnsOptions() {
      return this.columns.map((c) => ({
        text: c.label,
        value: c.key,
      }));
    },
    columnsToShowSet() {
      return new Set(this.columnsToShow);
    },
    shownColumns() {
      const columns = this.columns.filter(this.shouldShowColumn).map((column) => {
        column.cell = `cell(${column.key})`;
        column.head = `head(${column.key})`;
        if (column.filter) {
          // this slot name has to be lowercased per the documentation at
          // https://vuejs.org/guide/essentials/template-syntax.html#dynamic-argument-syntax-constraints
          column.filtertemplate = `filter(${column.key})`;
        }

        if (column.type === "id") {
          column.class = "tabular-nums text-right shrink-column";
        } else if (column.type === "currency" || column.type === "number") {
          column.class = "tabular-nums text-right";
        }

        return column;
      });

      if (this.showActionColumn) {
        columns.push({
          key: "actions",
          label: "Actions",
          tdClass: "table__cell__actions",
          cell: "cell(actions)",
          head: "head(actions)",
        });
      }

      return columns;
    },
    primaryKey() {
      const idColumn = this.columns.filter((c) => c.key === this.keyColumn);
      if (idColumn.length >= 1) {
        return idColumn[0].key;
      }

      return this.shownColumns[0].key;
    },
    hasFilters() {
      return this.allFilters.length > 0;
    },
    relationsToFetch() {
      const relationSet = new Set(this.loadRelations);

      for (const column of this.columns) {
        if (column.type !== "relation") {
          continue;
        }
        relationSet.add(column.key.substring(0, column.key.lastIndexOf(".")));
      }

      return Array.from(relationSet).join(",");
    },
    relationsToCount() {
      const relationSet = new Set(this.countRelations);

      for (const column of this.columns) {
        if (column.type !== "relation_count") {
          continue;
        }
        relationSet.add(column.key.replace("_count", ""));
      }

      return Array.from(relationSet).join(",");
    },
    allFilters() {
      const columnFilters = this.columns.filter((c) => c.filter).map((c) => c.filter);

      return [...columnFilters, ...this.extraFilters];
    },
    // Extra filters and filters for not-visible columns
    detachedFilters() {
      const columnFilters = this.columns
        .filter((c) => c.filter && !this.shouldShowColumn(c))
        .map((c) => c.filter);
      return [...columnFilters, ...this.extraFilters];
    },
    combinedParams() {
      const params = {
        ...this.filters,
        ...this.fetchParams,
        per_page: this.itemsPerPage,
        page: this.currentPage,
      };
      if (this.relationsToFetch) {
        params.relations = this.relationsToFetch;
      }
      if (this.relationsToCount) {
        params.count_relations = this.relationsToCount;
      }
      if (this.sortBy) {
        params.order = (this.sortDesc ? "-" : "") + this.sortBy;
      }
      return params;
    },
    activeFilterCount() {
      return Object.values(this.filters).filter((f) => f !== null).length;
    },
    differsFromDefault() {
      if (this.activeFilterCount !== Object.keys(this.initialFilters).length) {
        return true;
      }

      for (const filter in this.filters) {
        if (this.filters[filter] != this.initialFilters[filter]) {
          return true;
        }
      }

      const columnsToShowByDefault = this.columns.filter((c) => c.showByDefault).map((c) => c.key);
      if (columnsToShowByDefault.length !== this.columnsToShow.length) {
        return true;
      }

      for (const column of columnsToShowByDefault) {
        if (!this.columnsToShow.includes(column)) {
          return true;
        }
      }

      return false;
    },
  },
  watch: {
    combinedParams(newParams, oldParams) {
      if (JSON.stringify(newParams) !== JSON.stringify(oldParams)) {
        // We debounce here since params might change twice quickly: e.g. changing order direction
        // and param by clicking on a column header
        this.debouncedFetchItems();
      }
    },
    itemsPerPage() {
      this.updateState();
    },
    currentPage() {
      this.updateState();
    },
    sortBy() {
      this.updateState();
    },
    sortDesc() {
      this.updateState();
    },
    columnsToShow() {
      this.updateState();
    },
    filters: {
      handler: function () {
        this.currentPage = 1;
        if (this.syncFiltersWithUrl) {
          const setFilters = {};
          Object.keys(this.filters)
            .filter((key) => this.filters[key] !== undefined && this.filters[key] !== null)
            .forEach(
              (k) => (setFilters[this.urlPrefix ? `${this.urlPrefix}.${k}` : k] = this.filters[k])
            );

          let allNamespacedFilterKeys = new Set(
            this.allFilters
              .map((f) => f.field)
              .map((k) => (this.urlPrefix ? `${this.urlPrefix}.${k}` : k))
          );
          // Other unrelated params
          for (const queryKey in this.$route.query) {
            if (!allNamespacedFilterKeys.has(queryKey)) {
              setFilters[queryKey] = this.$route.query[queryKey];
            }
          }

          if (JSON.stringify(this.$route.query) !== JSON.stringify(setFilters)) {
            // Catch expected 'Redundant Navigation' error
            this.$router.replace({ query: setFilters }).catch(() => {});
          }
        }
        this.updateState();
        this.$emit("filters-changed", this.filters);
      },
      deep: true,
    },
    newItem(newValue, oldValue) {
      if (!newValue || !oldValue) {
        const fetchedCopy = structuredClone(this.fetchedItems);
        this.items = newValue ? [structuredClone(newValue), ...fetchedCopy] : fetchedCopy;
        this.itemsCopy = structuredClone(this.items);
      }
    },
    "$route.query": function () {
      if (!this.syncFiltersWithUrl) {
        return;
      }
      const newFilters = this.getFiltersFromUrl();
      for (const filterKey in newFilters) {
        // On query param change, only update filters if any differ
        if (this.filters[filterKey] !== newFilters[filterKey]) {
          this.filters = newFilters;
          return;
        }
      }
    },
  },
  beforeCreate() {
    uniqueId++;
  },
  created() {
    this.debouncedFetchItems = debounce(this.fetchItems, 16);
  },
  activated() {
    // page may be activated with new url params
    let updatedWithParams = false;
    if (this.syncFiltersWithUrl) {
      const urlFilters = this.getFiltersFromUrl();
      if (Object.keys(urlFilters).length > 0) {
        this.filters = urlFilters;
        updatedWithParams = true;
      }
    }

    // refresh the table if reactivated after more than 15 minutes of idleness
    if (
      updatedWithParams ||
      (this.lastFetch && this.$dayjs().diff(this.lastFetch, "minutes") > 15)
    ) {
      this.$nextTick(this.fetchItems);
    }
  },
  methods: {
    calculateTimeSinceLastFetch() {
      return (this.minutesSinceLastFetch = dayjs().diff(this.lastFetch, "minutes"));
    },

    shouldShowColumn(column) {
      return this.columnsToShowSet.has(column.key);
    },
    // to set filters programatically
    setFilters(filters) {
      this.filters = filters;
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    getFiltersFromUrl() {
      const urlFilters = this.$route.query;
      const filters = {};

      // We cannot use the computed allFilters value, since this is called from data()
      const columnFilters = this.columns.filter((c) => c.filter).map((c) => c.filter);

      for (const filter of [...columnFilters, ...this.extraFilters]) {
        const urlFilterKey = this.urlPrefix ? `${this.urlPrefix}.${filter.field}` : filter.field;

        if (urlFilterKey in urlFilters) {
          filters[filter.field] = urlFilters[urlFilterKey];
        }
      }

      return filters;
    },
    resetToDefaults() {
      this.currentPage = 1;
      this.filters = { ...this.initialFilters };
      this.columnsToShow = this.columns.filter((c) => c.showByDefault).map((c) => c.key);
    },
    getSavedColumnsToShowOrDefault(listState) {
      if (listState?.columnsToShow) {
        const validColumnKeys = new Set(this.columns.map((c) => c.key));
        const invalidColumnsToShow = listState.columnsToShow.filter((f) => !validColumnKeys.has(f));
        if (invalidColumnsToShow.length === 0) {
          return listState.columnsToShow;
        }
      }
      return this.columns.filter((c) => c.showByDefault).map((c) => c.key);
    },
    getSavedFiltersOrDefault(listState) {
      if (listState?.filters) {
        const validFilterFields = new Set(
          [...this.extraFilters, ...this.columns.filter((c) => c.filter).map((c) => c.filter)].map(
            (f) => f.field
          )
        );
        const invalidFilters = Object.keys(listState.filters).filter(
          (f) => !validFilterFields.has(f)
        );
        if (invalidFilters.length === 0) {
          return listState.filters;
        }
      }
      return { ...this.initialFilters };
    },
    refresh() {
      this.fetchItems();
    },
    selectAll() {
      this.$refs.table.selectAllRows();
    },
    rowSelected(event) {
      this.selectedCount = event.length;
      this.$emit("row-selected", event);
    },
    toggleSelection(val) {
      if (val === true) {
        this.$refs.table.selectAllRows();
      } else if (val === false) {
        this.$refs.table.clearSelected();
      }
    },
    async fetchItems() {
      this.$emit("updating");
      this.isBusy = true;
      try {
        const {
          data: { data, total, meta },
        } = await get(this.endpoint, {
          axiosRequestConfig: {
            params: this.combinedParams,
          },
          notifications: {
            action: this.$t("components.shared.paginated_table.notifications.loading.action"),
          },
        });
        this.itemCount = data.length;
        this.total = total ?? meta.total;
        this.lastFetch = this.$dayjs();
        this.isBusy = false;
        this.$emit("updated");
        this.showDetails = {};
        this.fetchedItems = data.map((item) => ({
          ...item,
          _showDetails: data.length === 1 && this.hasFilters && this.hasRowDetails,
          _changed: false,
        }));

        const fetchedCopy = structuredClone(this.fetchedItems);
        this.items = this.newItem ? [structuredClone(this.newItem), ...fetchedCopy] : fetchedCopy;
        this.itemsCopy = structuredClone(this.items);
      } catch (e) {
        this.isBusy = false;
        this.fetchedItems = [];
        this.items = [];
        this.itemsCopy = [];
      }
    },
    isExternalUrl(column, item) {
      return column.urlFct(item).includes("http", 0);
    },
    isUrlValid(column, item) {
      if (!column || !column.urlFct) {
        return false;
      }

      try {
        return !!column.urlFct(item);
      } catch (e) {
        return false;
      }
    },
    rowClicked(item, index, event) {
      this.$emit("row-clicked", item);

      if (this.rowLink && !item.deleted_at) {
        if (event.ctrlKey) {
          const routeData = this.$router.resolve(`/admin/${this.endpoint}/${item[this.keyColumn]}`);
          window.open(routeData.href, "_blank");
        } else {
          this.$router.push(`/admin/${this.endpoint}/${item[this.keyColumn]}`);
        }
      }

      if (this.hasRowDetails && !this.rowDisabled(item)) {
        item._showDetails = !item._showDetails;
      }
    },
    updateState() {
      if (this.id) {
        this.$store.commit("paginatedTables/updateListState", {
          listId: this.id,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filters: this.filters,
          perPage: this.itemsPerPage,
          currentPage: this.currentPage,
          columnsToShow: this.columnsToShow,
        });
      }
    },
    computeRowClasses(item) {
      if (!item) {
        return;
      }
      let classes = [];
      if (item.deleted_at) {
        classes.push("row-item-deleted");
      }
      if (this.rowDisabled(item)) {
        classes.push("row-item-disabled");
      }

      return classes;
    },
    getFetchedItemCopy(item) {
      return this.itemsCopy.find((i) => i[this.keyColumn] === item[this.keyColumn]);
    },
    updateItem(item) {
      if (!item || !item[this.keyColumn]) {
        return;
      }

      this.replaceOrPrependItem(this.fetchedItems, item);
      this.replaceOrPrependItem(this.items, item);
      this.replaceOrPrependItem(this.itemsCopy, structuredClone(item));
    },
    replaceOrPrependItem(list, item) {
      let index = list.findIndex((i) => i[this.keyColumn] === item[this.keyColumn]);
      if (index >= 0) {
        list.splice(index, 1, item);
      } else {
        list.unshift(item);
      }
    },
  },
};
</script>

<style lang="scss">
.refresh-button svg.b-icon {
  animation-duration: 1s;
}

.filter-list {
  .filter {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
  }

  &.card {
    margin: 0 0.4rem;
    position: relative;
    top: 0.5rem;
    border-radius: 0.5rem;
    background: transparentize($secondary, 0.9);
    box-shadow: inset 0 1px 3px transparentize($secondary, 0.5);
    border: 1px solid $light-grey;

    .card-body {
      padding: 1rem 1rem 0;

      .form-group {
        margin-bottom: 1rem;
        flex: 1;
      }
    }
  }
}

.paginated-table {
  &.table-responsive {
    margin-bottom: 0.5rem;
  }

  .details-chevron {
    color: $light;
    stroke: $light;
    stroke-width: 2;
    transition: transform 0.3s ease;

    &.details-showing {
      transform: rotate(90deg);
    }
  }

  &:not(.with-filters) {
    margin-top: -0.5rem;
  }

  tbody tr {
    &:not(.b-table-empty-row):not(.row-item-disabled) td {
      background: $white;
    }

    &.row-item-disabled {
      background-color: rgb(232, 234, 236);
      &:hover {
        background-color: rgb(232, 234, 236);
      }
      opacity: 0.8;
    }
    &.b-table-empty-row {
      background: $eggshell;
      td {
        color: $grey;
        border: 1px solid $light-grey;
        padding: 0.2rem;
      }
    }
  }
  .table {
    border-collapse: separate;
    border-spacing: 0 0.4rem;
    padding: 0 4px;

    // Show loading text when table refreshing witout any content
    &[aria-busy="true"] .b-table-empty-row td div {
      > div {
        display: none;
      }
      &:after {
        display: block;
        content: "...";
        text-align: center;
      }
    }

    &.row-clickable
      tbody
      tr:not(.b-table-empty-row):not(.b-table-details):not(.row-item-deleted):not(
        .row-item-disabled
      ) {
      cursor: pointer;

      &:hover {
        box-shadow:
          $small-shadow,
          0 0 1px 1px $locomotion-green;
        .details-chevron {
          color: $locomotion-green;
          stroke: $locomotion-green;
        }
      }
    }

    tbody {
      tr:not(.b-table-details) td {
        height: 3rem;
        font-weight: 600;
      }
      tr {
        td {
          position: relative;

          &:first-child {
            border-top-left-radius: 8px;
            border-bottom-left-radius: 8px;
          }

          &:last-child {
            border-top-right-radius: 8px;
            border-bottom-right-radius: 8px;
          }
        }

        &:not(.b-table-empty-row) {
          box-shadow: $small-shadow;
          border-radius: 8px;
        }

        &.b-table-details {
          position: relative;
          top: -0.55rem;
          box-shadow: $small-shadow;
          border-top-right-radius: 0;
          border-top-left-radius: 0;
          td:first-child,
          td:last-child {
            border-top-right-radius: 0;
            border-top-left-radius: 0;
          }
          td {
            padding: 0;
            height: 0;
            // Animate detail rows opening by scaling the height and vertical padding from 0 to
            // the final value.
            overflow: clip;
            > * {
              height: auto;
              transition: all 0.3s ease;
              @starting-style {
                opacity: 0;
                height: 0;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
              }
            }
          }
        }
        &.b-table-has-details {
          box-shadow: $small-shadow;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          td:first-child,
          td:last-child {
            border-bottom-right-radius: 0;
            border-bottom-left-radius: 0;
          }
        }

        &.b-table-has-details:hover + tr {
          box-shadow:
            $small-shadow,
            1px 1px 1px $locomotion-green,
            -1px 1px 1px $locomotion-green;
        }
        &.b-table-has-details:hover {
          box-shadow:
            $small-shadow,
            0 0 1px 1px $locomotion-green;
        }

        &.table-active {
          td {
            background-color: #ecfffe;
          }
          &:hover td {
            background: #ecfffe !important;
          }
        }
      }
    }

    thead {
      tr {
        box-shadow: none;
        top: 0.5rem;
        height: 1px;
        @supports (height: -moz-available) {
          height: 100%;
        }
      }
      th {
        font-size: 0.85rem;
        background: transparent;
        color: $content-neutral-secondary;
        padding: 0.75rem 0.5rem;
        font-weight: 400;

        @supports (height: -moz-available) {
          height: 100%;
        }
      }
    }

    th,
    td {
      height: 1px;
      padding: 0.4rem;
      font-size: 0.9rem;
      color: $dark;

      select,
      input,
      textarea {
        font-size: 0.9rem;
      }

      &.shrink-column {
        width: 1%;

        .form-control {
          min-width: 4rem;
          width: auto;
        }
      }
    }

    .header-wrapper {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      .header-label {
        white-space: nowrap;
      }
    }

    td {
      vertical-align: middle;
      .select-wrapper {
        cursor: pointer;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        &:hover .custom-control-label::before {
          border-color: $locomotion-green;
        }
      }
    }

    th {
      vertical-align: top;
    }

    // Move order toggles to right when text is right aligned.
    th {
      padding-right: 0.2rem !important;
      padding-left: 0.2rem !important;
      background-position: left calc(0.75rem / 2) top 0.8rem !important;
    }
    th.b-table-sort-icon-left .header-label {
      padding-left: 1rem;
    }
    th.b-table-sort-icon-left.text-right {
      background-position: right calc(0.75rem / 2) top 0.8rem !important;
    }
    th.b-table-sort-icon-left.text-right .header-label {
      padding-right: 1rem;
      padding-left: 0;
    }
  }
}
</style>
